import { ApiClient } from './client';
import type { ApiEnvelope, ApiResponse } from './types';
import { unwrapApiResponseData } from './types';
import { translateClient } from '@/lib/i18n/client';
import type {
  CreateCustomStyleRequest,
  BatchCreateCustomStylesRequest,
  BatchCreateCustomStylesResult,
  CustomStyle,
  UpdateCustomStyleRequest,
  Creativity,
  StyleGenerationPreferenceResult,
} from '@/types/custom-style';

import type { StyleDraftInput, StyleEditorState, StyleEditorSaveResult, StyleElementTarget } from '@/types/style-exploration';

interface SuccessEnvelope<T> {
  success: boolean;
  data?: T;
  error?: string;
}

class CustomStylesApi extends ApiClient {
  async saveGenerationPreference(id: string, creativity: Creativity, options?: {expected_revision?: number; supersede_conversation?: boolean}): Promise<ApiResponse<StyleGenerationPreferenceResult>> {
    return this.unwrap(await this.request(`/custom-styles/${encodeURIComponent(id)}/generation-preference`, {
      method: 'PUT', body: JSON.stringify({ creativity, ...options }),
    }));
  }

  private unwrap<T>(response: ApiResponse<SuccessEnvelope<T> | T>): ApiResponse<T> {
    return unwrapApiResponseData<T>(response as ApiResponse<ApiEnvelope<T> | T>, {
      code: 'API_ERROR',
      message: translateClient('errors', 'common.fallbackMessage'),
    });
  }

  async editor(id: string): Promise<ApiResponse<StyleEditorState>> {
    return this.unwrap(await this.request(`/custom-styles/${encodeURIComponent(id)}/editor`));
  }

  async saveDraft(id: string, expected_revision: number, draft: StyleDraftInput): Promise<ApiResponse<StyleEditorState>> {
    return this.unwrap(await this.request(`/custom-styles/${encodeURIComponent(id)}/editor/draft`, {
      method: 'PUT', body: JSON.stringify({expected_revision, ...draft}),
    }));
  }

  async explore(id: string, payload: {expected_revision: number; dimension?: string; target?: StyleElementTarget | null; instruction: string; language: 'zh' | 'en'}): Promise<ApiResponse<StyleEditorState>> {
    return this.unwrap(await this.request(`/custom-styles/${encodeURIComponent(id)}/editor/explore`, {
      method: 'POST', body: JSON.stringify(payload),
    }));
  }

  async observe(id: string, expected_revision: number, language: 'zh' | 'en'): Promise<ApiResponse<StyleEditorState>> {
    return this.unwrap(await this.request(`/custom-styles/${encodeURIComponent(id)}/editor/observe`, {
      method: 'POST', body: JSON.stringify({expected_revision, language}),
    }));
  }

  async saveVersion(id: string, payload: {expected_revision: number; mode: 'version' | 'copy'; name?: string}): Promise<ApiResponse<StyleEditorSaveResult>> {
    return this.unwrap(await this.request(`/custom-styles/${encodeURIComponent(id)}/editor/save`, {
      method: 'POST', body: JSON.stringify(payload),
    }));
  }

  async prepare(id: string, expected_revision: number, language: 'zh' | 'en'): Promise<ApiResponse<StyleEditorState>> {
    return this.unwrap(await this.request(`/custom-styles/${encodeURIComponent(id)}/editor/prepare`, {
      method: 'POST', body: JSON.stringify({expected_revision, language}),
    }));
  }

  async discardDraft(id: string, expected_revision: number): Promise<ApiResponse<StyleEditorState>> {
    return this.unwrap(await this.request(`/custom-styles/${encodeURIComponent(id)}/editor/discard`, {
      method: 'POST', body: JSON.stringify({expected_revision}),
    }));
  }

  async restoreVersion(id: string, payload: {expected_revision: number; version: number}): Promise<ApiResponse<StyleEditorState>> {
    return this.unwrap(await this.request(`/custom-styles/${encodeURIComponent(id)}/editor/restore`, {
      method: 'POST', body: JSON.stringify(payload),
    }));
  }

  async list(): Promise<ApiResponse<CustomStyle[]>> {
    return this.unwrap(await this.request<SuccessEnvelope<CustomStyle[]>>('/custom-styles'));
  }

  async create(payload: CreateCustomStyleRequest): Promise<ApiResponse<CustomStyle>> {
    return this.unwrap(await this.request<SuccessEnvelope<CustomStyle>>('/custom-styles', {
      method: 'POST',
      body: JSON.stringify(payload),
    }));
  }

  async createBatch(payload: BatchCreateCustomStylesRequest): Promise<ApiResponse<BatchCreateCustomStylesResult>> {
    return this.unwrap(await this.request('/custom-styles/batch', {
      method: 'POST', body: JSON.stringify(payload),
    }));
  }

  async update(id: string, payload: UpdateCustomStyleRequest): Promise<ApiResponse<CustomStyle>> {
    return this.unwrap(await this.request<SuccessEnvelope<CustomStyle>>(`/custom-styles/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }));
  }

  async archive(id: string): Promise<ApiResponse<{ id: string; status: 'archived' }>> {
    return this.unwrap(await this.request<SuccessEnvelope<{ id: string; status: 'archived' }>>(`/custom-styles/${id}`, {
      method: 'DELETE',
    }));
  }

  async retryAnalysis(id: string): Promise<ApiResponse<CustomStyle>> {
    return this.unwrap(await this.request<SuccessEnvelope<CustomStyle>>(`/custom-styles/${id}/retry-analysis`, {
      method: 'POST',
    }));
  }
}

export const customStylesApi = new CustomStylesApi();
export { CustomStylesApi };
